import React, { useState, useMemo, useEffect } from 'react';
import { Button, Switch } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { sortBy } from 'lodash';
import { useForm } from 'react-hook-form';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import PageContainer from 'components/PageContainer';
import { useGetRole } from 'hooks/ui/useGetRole';
import useCompanies from 'hooks/data/company/useCompanies';
import { useFunds } from 'hooks/data/fund/useFunds';
import { useFirm } from 'hooks/data/firm/useFirm';
import { useNotification } from 'hooks/ui/useNotification';
import FullPageSuccess from 'components/FullPageSuccess';
import { ADD_CUSTOM_UPDATE, EDIT_CUSTOM_UPDATE } from 'adminConstants/routes';
import { useCustomUpdate } from 'hooks/data/customUpdate/useCustomUpdate';
import { getCompanyLabel } from 'further-ui/utils';
import AddCustomUpdateDetails from './AddDetails';
import Summary from './Summary';
import ReportingDraftButtons from 'components/ReportingDraftButtons';
import { DeliveryType } from 'further-types/custom-updates';
import PageContent from 'components/PageContent';
import { Api } from 'further-types/custom-updates';
import NotifyRecipientsTooltip from './NotifyRecipientsTooltip';
import apiTrpc from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import {
  InvestmentCustomTagCategory,
  CustomTag as InvestmentCustomTag,
} from 'further-types/investment';
import { InvestorCustomTagCategory } from 'further-types/investor';

const isInvestmentCustomTag = (
  tag:
    | InvestmentCustomTag
    | { tag: string; category: InvestorCustomTagCategory },
): tag is InvestmentCustomTag => {
  return Object.values(InvestmentCustomTagCategory).includes(
    tag.category as InvestmentCustomTagCategory,
  );
};

const isInvestorCustomTag = (
  tag:
    | InvestmentCustomTag
    | { tag: string; category: InvestorCustomTagCategory },
): tag is { tag: string; category: InvestorCustomTagCategory } => {
  return Object.values(InvestorCustomTagCategory).includes(
    tag.category as InvestorCustomTagCategory,
  );
};

interface CreateCustomUpdateRequest
  extends Omit<Api.CreateCustomUpdateRequest, 'recipientFilter'> {
  recipients: Array<Api.Recipient>;
  recipientFilter: Api.RecipientFilter | '';
  _id?: string;
}

// Build discriminated mutation input based on recipientFilter
function buildRecipientsRequest(form: CreateCustomUpdateRequest) {
  const base = {
    firmId: form.firmId,
    companyId: form.companyId,
    trancheId: form.trancheId,
    investorIds: form.investorIds,
    allocationIds: form.allocationIds,
    sendToDirectInvestors: form.sendToDirectInvestors,
    sendToAdvisedInvestors: form.sendToAdvisedInvestors,
    sendToAdvisers: form.sendToAdvisers,
    sendToOrganisations: form.sendToOrganisations,
    sendToAllUsers: form.sendToAllUsers,
    sendToAllUsersWithSubscriptions: form.sendToAllUsersWithSubscriptions,
    sendToAllUsersWithNoSubscriptions: form.sendToAllUsersWithNoSubscriptions,
    deliveryToInvestors: form.deliveryToInvestors,
  };

  switch (form.recipientFilter) {
    case Api.RecipientFilter.SubscriptionCustomTags:
      return {
        ...base,
        recipientFilter: Api.RecipientFilter.SubscriptionCustomTags,
        customTags: form.customTags.filter(isInvestmentCustomTag),
      };
    case Api.RecipientFilter.InvestorCustomTags:
      return {
        ...base,
        recipientFilter: Api.RecipientFilter.InvestorCustomTags,
        customTags: form.customTags.filter(isInvestorCustomTag),
      };
    default:
      return {
        ...base,
        recipientFilter: form.recipientFilter as Exclude<
          Api.RecipientFilter,
          | Api.RecipientFilter.SubscriptionCustomTags
          | Api.RecipientFilter.InvestorCustomTags
        >,

        customTags: [],
      };
  }
}

export type CustomUpdateStyleClasses = ReturnType<typeof useStyles>['classes'];

export type MappedTranche = {
  _id: string;
  label: string;
  firmId: {
    _id: string;
    firmName: string;
  };
};

export type CompanyWithLabel = {
  _id: string;
  label?: string | null;
  firm?: {
    _id: string;
    firmName: string;
  };
};

const useStyles = makeStyles()((theme) => ({
  cardContentPadding: {
    padding: '2rem',
  },
  errorText: {
    color: theme.palette.error.main,
    marginTop: '4px',
    fontSize: '0.65625rem',
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: 20,
  },
  buttonLeftContainer: {
    display: 'flex',
    alignItems: 'center',
    gridGap: '1rem',
  },
  buttonRightContainer: {
    display: 'flex',
    alignItems: 'center',
    gridGap: '1rem',
  },
  buttonLeftMargin: {
    marginLeft: 12,
  },
  divider: {
    margin: '30px 0',
  },
  error: {
    color: theme.palette.error.main,
    padding: '5px 0 0 14px',
  },
  attachmentLink: {
    fontSize: '12px',
    color: theme.palette.text.primary,

    '&:hover': {
      color: theme.palette.text.rootColor,
    },
  },
}));

const AddCustomUpdate: React.FC = () => {
  const { firmId, isSuperAdmin } = useGetRole();
  const { data: companies } = useCompanies({
    params: isSuperAdmin ? {} : { firmId },
  });
  const { tranches } = useFunds({ fundStatus: [0, 1] });
  const { firms } = useFirm({ params: {} });
  const history = useHistory();
  const { id: customUpdateId } = useParams<{ id: string }>();
  const isEditPage = useRouteMatch(EDIT_CUSTOM_UPDATE)?.isExact;
  const steps = { AddDetails: 1, Summary: 2 };
  const [activeStep, setActiveStep] = useState(steps.AddDetails);
  const [showSuccess, setShowSuccess] = useState(false);
  const notification = useNotification();
  const getRecipientsToSendUpdate =
    apiTrpc.customUpdate.getRecipientsToSendUpdate.useMutation(
      useHandleTrpcMutation()({}),
    );
  const isGetRecipientsToSendUpdatePending =
    getRecipientsToSendUpdate.isPending;
  const breadcrumbs = [
    { label: 'Dashboard', link: '/' },
    {
      label: 'Investor Reporting',
      link: '/investor-reporting',
    },
    {
      label: isEditPage ? 'Edit custom update' : 'Create custom update',
      link: '/',
      isActive: true,
    },
  ];
  const companiesWithLabel: Array<CompanyWithLabel> = useMemo(
    () =>
      (companies?.result ?? []).map((company) => ({
        label: getCompanyLabel({
          ...company,
          firmId: company.firm,
          fundId: company.fund,
        }),
        _id: company._id,
        firm: company?.firm,
      })),
    [companies?.result],
  );

  const defaultValues: CreateCustomUpdateRequest = {
    firmId: '',
    companyId: [],
    trancheId: [],
    subject: '',
    title: '',
    sendToAllUsers: false,
    sendToAllUsersWithSubscriptions: false,
    sendToAllUsersWithNoSubscriptions: false,
    sendToDirectInvestors: false,
    sendToAdvisedInvestors: false,
    sendToAdvisers: false,
    sendToOrganisations: false,
    deliveryToInvestors: DeliveryType.Email,
    emailBody: '',
    attachments: [],
    investorIds: [],
    allocationIds: [],
    recipients: [],
    recipientFilter: '',
    customTags: [],
    status: Api.Status.Draft,
    notifyRecipientsOnEdit: false,
  };

  const { getValues, setValue, watch } = useForm<CreateCustomUpdateRequest>({
    defaultValues,
    mode: 'onChange',
  });

  const [errorText, setErrorText] = useState({});
  const { classes } = useStyles();
  const {
    createCustomUpdate: { mutate: createMutate, isPending },
    updateCustomUpdate: { mutate: updateMutate, isPending: isUpdatePending },
    fetchCustomUpdate,
  } = useCustomUpdate();

  // Fetch existing custom update data when in edit mode
  const { data: existingCustomUpdate } = fetchCustomUpdate(customUpdateId);

  // Initialize firm selection for non-superadmin users
  useEffect(() => {
    if (firmId && !getValues().firmId) {
      setValue('firmId', firmId);
    }
  }, [firmId, setValue, getValues]);

  // Populate form with existing data when in edit mode
  useEffect(() => {
    if (isEditPage && existingCustomUpdate) {
      setValue('_id', existingCustomUpdate._id);

      // Populate form fields with existing data
      setValue('subject', existingCustomUpdate.subject || '');
      setValue('title', existingCustomUpdate.title || '');
      setValue('emailBody', existingCustomUpdate.emailBody || '');
      setValue(
        'deliveryToInvestors',
        existingCustomUpdate.deliveryToInvestors || '',
      );
      setValue('attachments', existingCustomUpdate.attachments || []);
      setValue(
        'sendToDirectInvestors',
        existingCustomUpdate.sendToDirectInvestors || false,
      );
      setValue(
        'sendToAdvisedInvestors',
        existingCustomUpdate.sendToAdvisedInvestors || false,
      );
      setValue('sendToAdvisers', existingCustomUpdate.sendToAdvisers || false);
      setValue(
        'sendToOrganisations',
        existingCustomUpdate.sendToOrganisations || false,
      );
      setValue('recipientFilter', existingCustomUpdate.recipientFilter || '');
      setValue('firmId', existingCustomUpdate.firmId || '');

      // Set recipient-related fields - only set if data exists
      // Filter out any invalid investor IDs to prevent Autocomplete errors
      const validInvestorIds = (existingCustomUpdate.investorIds || []).filter(
        Boolean,
      );
      setValue('investorIds', validInvestorIds);
      setValue('customTags', existingCustomUpdate.customTags || []);
      setValue('allocationIds', existingCustomUpdate.allocationIds || []);
      setValue('companyId', existingCustomUpdate.companyIds || []);
      setValue('trancheId', existingCustomUpdate.fundIds || []);

      // Set recipient selection flags
      setValue('sendToAllUsers', existingCustomUpdate.sendToAllUsers || false);
      setValue(
        'sendToAllUsersWithSubscriptions',
        existingCustomUpdate.sendToAllUsersWithSubscriptions || false,
      );
      setValue(
        'sendToAllUsersWithNoSubscriptions',
        existingCustomUpdate.sendToAllUsersWithNoSubscriptions || false,
      );

      // Set status
      setValue('status', existingCustomUpdate.status || Api.Status.Submitted);
    }
  }, [isEditPage, existingCustomUpdate, setValue, firms?.data?.result]);

  const heading = isEditPage
    ? 'Investor reporting: Edit custom update'
    : 'Investor reporting: Create custom update';

  const mappedTranche: Array<MappedTranche> = (tranches.data ?? []).map(
    (tranche) => ({
      _id: tranche._id,
      label: `${tranche.firmId.firmName} ${tranche.fundName}`,
      firmId: tranche.firmId,
    }),
  );
  const sortedTranche = sortBy(mappedTranche ?? [], (tranche) =>
    tranche.label.toLowerCase(),
  );
  const sortedCompany = companiesWithLabel
    ? sortBy(companiesWithLabel, (c) => c.label?.toLowerCase())
    : [];

  const validateFields = (formData: CreateCustomUpdateRequest) => {
    const errorMessage: Record<string, string> = {};
    if (isSuperAdmin && !formData.firmId) {
      errorMessage.firmId = 'Please select a firm.';
    }
    if (!formData.recipientFilter) {
      errorMessage.recipientFilter = 'Please select a recipient filter.';
    }
    if (!formData.subject) {
      errorMessage.subject = 'Please enter subject line.';
    }
    if (!formData.title) {
      errorMessage.title = 'Please enter email title.';
    }
    if (!formData.emailBody) {
      errorMessage.emailBody = 'Please enter email body.';
    }
    if (
      !formData.companyId.length &&
      !formData.trancheId.length &&
      !formData.investorIds.length &&
      !formData.sendToAllUsers &&
      !formData.sendToAllUsersWithSubscriptions &&
      !formData.sendToAllUsersWithNoSubscriptions &&
      !formData.customTags.length
    ) {
      errorMessage.companyId = 'Please select recipients.';
      errorMessage.investorIds = 'Please select recipients.';
    }

    if (
      formData.recipientFilter !== Api.RecipientFilter.Specific &&
      formData.recipientFilter !== Api.RecipientFilter.Subscriptions &&
      formData.recipientFilter !== Api.RecipientFilter.NoSubscriptions &&
      !formData.sendToDirectInvestors &&
      !formData.sendToAdvisedInvestors &&
      !formData.sendToAdvisers &&
      !formData.sendToOrganisations
    ) {
      errorMessage.sendTo = 'Please select recipients';
    }

    if (
      (formData.recipientFilter ===
        Api.RecipientFilter.SubscriptionCustomTags ||
        formData.recipientFilter === Api.RecipientFilter.InvestorCustomTags) &&
      !formData.customTags.length
    ) {
      errorMessage.customTags = 'Please select custom tags';
    }

    setErrorText(errorMessage);

    return Object.keys(errorMessage).length;
  };

  const quickSaveDraft = async () => {
    const formData = getValues();

    if (!formData.firmId) {
      notification.error('Please select a firm');
      return;
    }

    const draftData = {
      ...formData,
      firmId: formData.firmId,
      status: Api.Status.Draft,
      attachments: processAttachmentsForSubmission(formData.attachments || []),
    };

    if (formData._id) {
      updateMutate(
        { _id: formData._id, ...draftData },
        {
          onSuccess: (response) => {
            if (response.status === 200) {
              notification.success('Draft saved successfully');
            } else {
              notification.error((response as any).response.data.responseMsg);
            }
          },
          onError: (error: any) => {
            notification.error(
              error?.response?.data?.responseMsg || 'Failed to save draft',
            );
          },
        },
      );
    } else {
      createMutate(draftData, {
        onSuccess: (response) => {
          if (response.status === 200) {
            notification.success('Draft saved successfully');

            // Store the draft ID for future updates
            const responseData = response.data
              .data as Api.CreateCustomUpdateResponse;

            if (responseData.customUpdate._id) {
              setValue('_id', responseData.customUpdate._id);
            }
          } else {
            notification.error((response as any).response.data.responseMsg);
          }
        },
        onError: (error: any) => {
          notification.error(
            error?.response?.data?.responseMsg || 'Failed to save draft',
          );
        },
      });
    }
  };

  const saveDraftAndExit = async () => {
    const formData = getValues();

    if (!formData.firmId) {
      notification.error('Please select a firm');
      return;
    }

    const draftData = {
      ...formData,
      firmId: formData.firmId,
      status: Api.Status.Draft,
      attachments: processAttachmentsForSubmission(formData.attachments || []),
    };

    if (formData._id) {
      updateMutate(
        { _id: formData._id, ...draftData },
        {
          onSuccess: (response) => {
            if (response.status === 200) {
              notification.success('Draft saved successfully');
              history.push('/investor-reporting');
            } else {
              notification.error((response as any).response.data.responseMsg);
            }
          },
          onError: (error: any) => {
            notification.error(
              error?.response?.data?.responseMsg || 'Failed to save draft',
            );
          },
        },
      );
    } else {
      createMutate(draftData, {
        onSuccess: (response) => {
          if (response.status === 200) {
            notification.success('Draft saved successfully');
            history.push('/investor-reporting');
          } else {
            notification.error((response as any).response.data.responseMsg);
          }
        },
        onError: (error: any) => {
          notification.error(
            error?.response?.data?.responseMsg || 'Failed to save draft',
          );
        },
      });
    }
  };

  // Helper function to process attachments for submission
  const processAttachmentsForSubmission = (
    attachments: Array<Api.CustomUpdateAttachment>,
  ): Array<Api.CustomUpdateAttachment> => {
    return attachments.map((attachment) => ({
      title: attachment.title,
      url: attachment.url,
    }));
  };

  const onClick = async () => {
    const formData = getValues();

    if (steps.AddDetails === activeStep) {
      if (!validateFields(formData)) {
        if (!formData.firmId) {
          notification.error('Please select a firm');
          return;
        }

        const request = buildRecipientsRequest(formData);
        const response = await getRecipientsToSendUpdate.mutateAsync(request);

        setValue('firmId', formData.firmId);
        setValue('recipients', response.recipients);
        setActiveStep(steps.Summary);
      }
    } else {
      const finalData = {
        ...formData,
        status: Api.Status.Submitted,
        attachments: processAttachmentsForSubmission(
          formData.attachments || [],
        ),
      };

      if (formData._id) {
        updateMutate(
          { _id: formData._id, ...finalData },
          {
            onSuccess: (response) => {
              if (response.status === 200) {
                setShowSuccess(true);
              } else {
                notification.error((response as any).response.data.responseMsg);
              }
            },
            onError: (error: any) => {
              notification.error(
                error?.response?.data?.responseMsg || 'Failed to send update',
              );
            },
          },
        );
      } else {
        createMutate(finalData, {
          onSuccess: (response) => {
            if (response.status === 200) {
              setShowSuccess(true);
            } else {
              notification.error((response as any).response.data.responseMsg);
            }
          },
          onError: (error: any) => {
            notification.error(
              error?.response?.data?.responseMsg || 'Failed to send update',
            );
          },
        });
      }
    }
  };
  const formData = watch();
  const isTrancheSelected = formData.trancheId.length;
  const list = isTrancheSelected ? sortedTranche : sortedCompany;
  const selectedValues = isTrancheSelected
    ? formData.trancheId
    : formData.companyId;
  const selectedTranchesOrCompanies = selectedValues?.map(
    (id) => list.find((rec) => rec._id === id)?.label,
  );

  if (showSuccess) {
    return (
      <FullPageSuccess
        title="Your update has been successfully sent"
        additionalText="Your recipients will receive this update to the email address stored on their Further account, as well as their investor portal, depending on the settings you selected."
        proceedText="Send another update"
        proceedLink={ADD_CUSTOM_UPDATE}
      />
    );
  }

  const contentTitle =
    activeStep === steps.AddDetails
      ? 'Custom update details'
      : 'Custom update summary';

  const showNotifyRecipientsOnEdit =
    activeStep === steps.Summary &&
    formData?.deliveryToInvestors !== DeliveryType.Portal &&
    formData?.status === Api.Status.Submitted;

  return (
    <PageContainer heading={heading} breadcrumbs={breadcrumbs}>
      <PageContent header={contentTitle}>
        {steps.AddDetails === activeStep ? (
          <AddCustomUpdateDetails
            setValue={setValue}
            formData={formData}
            errorText={errorText}
            classes={classes}
            firmId={firmId}
          />
        ) : (
          <Summary
            data={formData}
            classes={classes}
            selectedTranchesOrCompanies={selectedTranchesOrCompanies}
          />
        )}

        <div className={classes.buttonContainer}>
          <div className={classes.buttonLeftContainer}>
            {activeStep === steps.Summary ? (
              <>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setActiveStep(steps.AddDetails);
                  }}
                >
                  Back
                </Button>
                {formData?.status !== Api.Status.Submitted && (
                  <Button
                    variant="outlined"
                    disabled={isPending || isUpdatePending}
                    onClick={saveDraftAndExit}
                  >
                    Save draft and exit
                  </Button>
                )}
              </>
            ) : (
              ''
            )}
          </div>
          <div className={classes.buttonRightContainer}>
            {showNotifyRecipientsOnEdit && (
              <NotifyRecipientsTooltip title="If toggled, the system will send an email to each applicable recipient with the update details.">
                <Switch
                  checked={formData?.notifyRecipientsOnEdit}
                  onChange={(event: any) =>
                    setValue('notifyRecipientsOnEdit', event.target.checked)
                  }
                />
              </NotifyRecipientsTooltip>
            )}
            <Button
              className={classes.buttonLeftMargin}
              color="primary"
              variant="contained"
              disabled={isGetRecipientsToSendUpdatePending || isPending}
              onClick={onClick}
              loading={isGetRecipientsToSendUpdatePending || isPending}
            >
              {activeStep === steps.AddDetails
                ? 'Proceed to Summary'
                : 'Send update'}
            </Button>
          </div>
        </div>

        {activeStep === steps.AddDetails &&
          (!formData?._id || formData?.status !== Api.Status.Submitted) && (
            <ReportingDraftButtons
              onQuickSave={quickSaveDraft}
              isQuickSaving={isPending || isUpdatePending}
              disabled={!formData.firmId}
            />
          )}
      </PageContent>
    </PageContainer>
  );
};
export default AddCustomUpdate;
